import type {
  CaseRequestHeaderResponse,
  CaseRequestFileMovementLogsResponse,
  CaseRequestWorkflowProgressResponse,
} from '../types/caseRequestDetails';
import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';

export {
  headerReferenceNo,
  headerCategory,
  headerSubCategory,
  headerTaskStatus,
} from '../utils/taskCaseDetailMappers';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function toApiResult<T>(res: Awaited<ReturnType<typeof caseApiRequest<T>>>): ApiResult<T> {
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}

/** CMS-CRC-20 — case request summary header for Task/Case Details. */
export async function getCaseRequestHeader(
  caseRequestId: string,
  taskId?: string,
): Promise<ApiResult<CaseRequestHeaderResponse>> {
  const id = caseRequestId.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }
  const trimmedTaskId = (taskId ?? '').trim();
  const qs =
    trimmedTaskId && UUID_RE.test(trimmedTaskId)
      ? `?${new URLSearchParams({ task_id: trimmedTaskId }).toString()}`
      : '';
  const res = await caseApiRequest<CaseRequestHeaderResponse>(
    `/header/${encodeURIComponent(id)}${qs}`,
  );
  return toApiResult(res);
}

/** CMS-CRC-21 — workflow stage progress for Task/Case Details sidebar. */
export async function getCaseRequestWorkflowProgress(
  userTaskId: string,
): Promise<ApiResult<CaseRequestWorkflowProgressResponse>> {
  const id = userTaskId.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid user task id.' };
  }
  const qs = new URLSearchParams({ user_task_id: id });
  const res = await caseApiRequest<CaseRequestWorkflowProgressResponse>(
    `/workflow-progress?${qs.toString()}`,
  );
  return toApiResult(res);
}

/** CMS-CRH-3 — physical file movement logs for File Movement Log tab. */
export async function getCaseRequestFileMovementLogs(
  caseRequestId: string,
): Promise<ApiResult<CaseRequestFileMovementLogsResponse>> {
  const id = caseRequestId.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }
  const res = await caseApiRequest<CaseRequestFileMovementLogsResponse>(
    `/${encodeURIComponent(id)}/file-movement-logs`,
  );
  return toApiResult(res);
}
